/**
 * Chimp - the player character.
 *
 * Two layers of position:
 *   - tileX/tileY: authoritative integer grid position. All game logic
 *     (collisions, win/lose checks) uses this.
 *   - visual container: tweens between snapped positions for smooth motion.
 *
 * Movement is one tile per call to tryMove(). Visual tween is short (less than
 * the input repeat interval) so held movement looks continuous but discrete.
 *
 * Rendering paths (auto-detected from the loaded chimp asset):
 *   - 2-frame spritesheet → Phaser Sprite playing the registered walk anim
 *     while moving, paused on frame 0 when idle
 *   - 1-frame image → static Sprite, no animation
 *   - no asset       → graphics-primitive placeholder
 * Facing direction is always handled via setFlipX, identical across paths.
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '../config';
import { Tile, type Playfield } from '../maze/types';
import type { Direction } from '../scroll/ScrollController';
import { AssetManager } from '../assets/AssetManager';

const MOVE_TWEEN_MS = 100;
/**
 * How long after a move the walk animation keeps playing before pausing.
 * Generously longer than the input repeat interval (110ms) so held movement
 * keeps the loop going seamlessly; a single tap shows ~1 full cycle and stops.
 */
const WALK_ANIM_LINGER_MS = 280;

/**
 * Facing direction. Source art faces DOWN by default; we rotate the sprite
 * to match the player's movement direction (see facingAngle() below).
 */
export type Facing = 'up' | 'down' | 'left' | 'right';

/** Rotation in degrees for each facing, given source art faces down. */
function facingAngle(f: Facing): number {
  switch (f) {
    case 'down':  return 0;
    case 'up':    return 180;
    case 'left':  return 90;
    case 'right': return -90;
  }
}

export class Chimp {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private gfx?: Phaser.GameObjects.Graphics;
  /** Sprite (animated or static) when the chimp asset loaded; else undefined. */
  private sprite?: Phaser.GameObjects.Sprite;
  /** Animation key (e.g. 'chimp-walk') if the loaded chimp has multiple frames. */
  private walkAnimKey: string | null = null;
  private moveTween?: Phaser.Tweens.Tween;
  /** When the walk anim should stop playing (scene time, ms). 0 = paused. */
  private walkUntilMs = 0;
  private facing: Facing = 'down';

  tileX: number;
  tileY: number;
  alive = true;

  constructor(scene: Phaser.Scene, startX: number, startY: number) {
    this.scene = scene;
    this.tileX = startX;
    this.tileY = startY;

    const children: Phaser.GameObjects.GameObject[] = [];
    if (AssetManager.has('chimp')) {
      this.sprite = scene.add.sprite(0, 0, 'chimp');
      // Source art is recommended 64x64 per frame; scale uniformly if not.
      const tex = this.sprite.texture.getSourceImage();
      // For spritesheets, getSourceImage returns the full image. The visible
      // frame size is determined by Phaser's spritesheet config (TILE_SIZE).
      // Display height is what matters for scaling.
      const frameH = this.sprite.frame.height;
      if (frameH !== TILE_SIZE) {
        this.sprite.setScale(TILE_SIZE / frameH);
      } else if (tex.height !== TILE_SIZE) {
        // Defensive: shouldn't happen for animated assets, but if a static
        // image is non-square handle it.
        this.sprite.setScale(TILE_SIZE / Math.max(tex.width, tex.height));
      }
      this.walkAnimKey = AssetManager.walkAnimKey('chimp');
      children.push(this.sprite);
    } else {
      this.gfx = scene.add.graphics();
      children.push(this.gfx);
    }
    this.container = scene.add.container(
      startX * TILE_SIZE + TILE_SIZE / 2,
      startY * TILE_SIZE + TILE_SIZE / 2,
      children
    );
    this.container.setDepth(50);
    this.draw();
  }

  /**
   * Attempt to move one tile in the given direction.
   * Returns the new direction if move succeeded (so caller can react),
   * or null if blocked.
   */
  tryMove(dir: Direction, pf: Playfield): Direction | null {
    if (!this.alive) return null;

    let nx = this.tileX;
    let ny = this.tileY;
    switch (dir) {
      case 'up':    ny--; this.facing = 'up';    break;
      case 'down':  ny++; this.facing = 'down';  break;
      case 'left':  nx--; this.facing = 'left';  break;
      case 'right': nx++; this.facing = 'right'; break;
    }

    // Out of playfield bounds — blocked (NOT a death; viewport-edge death
    // is a separate concept handled by the scene).
    if (nx < 0 || nx >= pf.width || ny < 0 || ny >= pf.height) return null;

    // Foliage blocks movement (chimp doesn't climb through dense jungle).
    if (pf.tiles[ny * pf.width + nx] === Tile.Foliage) return null;

    this.tileX = nx;
    this.tileY = ny;
    this.tweenToCurrentTile();
    this.startOrExtendWalk();
    this.draw();
    return dir;
  }

  /**
   * Per-frame tick from the scene. Stops the walk animation once the linger
   * window expires. Cheap; only does work if anims are in use.
   */
  tick(sceneTimeMs: number): void {
    if (!this.sprite || !this.walkAnimKey) return;
    if (this.walkUntilMs > 0 && sceneTimeMs >= this.walkUntilMs) {
      this.walkUntilMs = 0;
      // Pause on first frame so the chimp has a recognizable "idle" pose.
      this.sprite.stop();
      this.sprite.setFrame(0);
    }
  }

  /**
   * Force-set the chimp's tile position (no animation). Used for respawn.
   */
  reset(x: number, y: number): void {
    this.tileX = x;
    this.tileY = y;
    this.alive = true;
    this.facing = 'down';
    this.walkUntilMs = 0;
    if (this.sprite && this.walkAnimKey) {
      this.sprite.stop();
      this.sprite.setFrame(0);
    }
    if (this.moveTween) this.moveTween.stop();
    this.container.setPosition(
      x * TILE_SIZE + TILE_SIZE / 2,
      y * TILE_SIZE + TILE_SIZE / 2
    );
    this.container.setAlpha(1);
    this.draw();
  }

  kill(): void {
    if (!this.alive) return;
    this.alive = false;
    // Stop walk anim on death so the corpse doesn't keep stepping.
    if (this.sprite && this.walkAnimKey) this.sprite.stop();
    this.walkUntilMs = 0;
    // Quick visual confirmation — flash to red, fade out.
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0.2,
      scale: 0.6,
      duration: 250,
      ease: 'Sine.easeIn',
    });
  }

  /** World pixel position (center of current tile). */
  get worldX(): number {
    return this.tileX * TILE_SIZE + TILE_SIZE / 2;
  }
  get worldY(): number {
    return this.tileY * TILE_SIZE + TILE_SIZE / 2;
  }

  destroy(): void {
    // Safe against double-destroy / destroy-after-scene-shutdown:
    // Phaser tween/container may already be invalidated. Catch and proceed.
    try {
      if (this.moveTween) this.moveTween.stop();
    } catch {
      // tween manager already torn down
    }
    this.moveTween = undefined;
    try {
      this.container.destroy();
    } catch {
      // container already destroyed by scene shutdown
    }
  }

  // --- Internals --------------------------------------------------------

  private tweenToCurrentTile(): void {
    if (this.moveTween && this.moveTween.isPlaying()) this.moveTween.stop();
    this.moveTween = this.scene.tweens.add({
      targets: this.container,
      x: this.worldX,
      y: this.worldY,
      duration: MOVE_TWEEN_MS,
      ease: 'Sine.easeOut',
    });
  }

  /**
   * Kick the walk animation back into play (if we have one) and extend its
   * stop time by WALK_ANIM_LINGER_MS from now. Held-movement repeatedly calls
   * this, keeping the loop alive; single moves let it run briefly then pause.
   */
  private startOrExtendWalk(): void {
    if (!this.sprite || !this.walkAnimKey) return;
    this.walkUntilMs = this.scene.time.now + WALK_ANIM_LINGER_MS;
    if (!this.sprite.anims.isPlaying) {
      this.sprite.play(this.walkAnimKey);
    }
  }

  private draw(): void {
    // Rotation goes on the container, not the sprite, so both rendering
    // paths (sprite and placeholder graphics) get the same treatment and
    // the death-tween's scale on the container composes cleanly.
    this.container.setAngle(facingAngle(this.facing));

    if (this.sprite) {
      // No per-sprite flip — source art faces DOWN by convention and the
      // container's rotation handles all four facings.
      this.sprite.setFlipX(false);
      return;
    }
    // Placeholder path: redraw graphics primitives. Drawn "facing down"
    // (eyes on the upper half, mouth lower) so it matches the source-art
    // convention used by the sprite path.
    if (!this.gfx) return;
    const g = this.gfx;
    g.clear();
    g.fillStyle(0x6a4220, 1);
    g.fillCircle(0, 4, TILE_SIZE * 0.34);
    g.fillStyle(0xe8c87a, 1);
    g.fillCircle(0, -2, TILE_SIZE * 0.26);
    g.fillStyle(0xf3d9a5, 1);
    g.fillEllipse(0, 6, TILE_SIZE * 0.28, TILE_SIZE * 0.18);
    g.fillStyle(0x0a0a0a, 1);
    g.fillCircle(-7, -6, 2.6);
    g.fillCircle(7, -6, 2.6);
    g.fillStyle(0x3a1f0a, 1);
    g.fillRect(-5, 9, 10, 2);
    g.fillStyle(0x6a4220, 1);
    g.fillCircle(-16, -8, 4);
    g.fillCircle(16, -8, 4);
  }
}
